import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { VehicleCard } from "@/components/VehicleCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { currency } from "@/lib/status";
import { ShieldCheck, MapPin, Hash, Palette, Package, ArrowLeft, AlertTriangle, X, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { promoForVehicle, PROMO_TYPES, promoBadge, expiryLabel } from "@/lib/promotions";
import { toast } from "sonner";

const FALLBACK = "https://images.unsplash.com/photo-1533630217389-3a5e4dff5683?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [v, setV] = useState(null);
  const [similar, setSimilar] = useState([]);
  const [busy, setBusy] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [promos, setPromos] = useState([]);
  const touchX = useRef(null);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then(({ data }) => setV(data)).catch(() => navigate("/inventory"));
    api.get(`/vehicles/${id}/similar`).then(({ data }) => setSimilar(data)).catch(() => {});
    api.post(`/vehicles/${id}/view`).catch(() => {});
    api.get("/promotions/active").then(({ data }) => setPromos(data || [])).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!lightboxOpen) return;
    const len = (v && v.images && v.images.length) ? v.images.length : 1;
    const onKey = (e) => {
      if (e.key === "Escape") setLightboxOpen(false);
      if (e.key === "ArrowRight") setImgIdx((i) => (i + 1) % len);
      if (e.key === "ArrowLeft") setImgIdx((i) => (i - 1 + len) % len);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, v]);

  const request = async () => {
    if (!user) { navigate("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/requests", { source: "inventory", vehicle_id: id });
      toast.success("Request submitted — our broker team will verify availability.");
      navigate(`/requests/${data.id}`);
    } catch (e) {
      toast.error("Could not submit request");
    } finally { setBusy(false); }
  };

  if (!v) return <div className="min-h-screen bg-slate-50 dark:bg-slate-950"><Navbar /></div>;
  const sold = v.availability !== "available";
  const promo = promoForVehicle(promos, v);
  const gallery = v.images && v.images.length ? v.images : (v.image_url ? [v.image_url] : [FALLBACK]);
  const activeIdx = Math.min(imgIdx, gallery.length - 1);
  const showNext = () => setImgIdx((i) => (Math.min(i, gallery.length - 1) + 1) % gallery.length);
  const showPrev = () => setImgIdx((i) => (Math.min(i, gallery.length - 1) - 1 + gallery.length) % gallery.length);

  const specs = [
    { icon: Hash, label: "Stock #", value: v.stock_number || "—" },
    { icon: Hash, label: "VIN", value: v.vin || "Provided at verification" },
    { icon: Palette, label: "Color", value: v.color || "—" },
    { icon: MapPin, label: "Dealer location", value: v.dealer_location || "California" },
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <button onClick={() => navigate("/inventory")} className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 dark:text-slate-400 hover:text-slate-900" data-testid="back-btn">
          <ArrowLeft className="h-4 w-4" /> Back to inventory
        </button>

        <div className="grid gap-8 lg:grid-cols-2">
          <div>
            <button type="button" onClick={() => setLightboxOpen(true)} data-testid="open-lightbox" className="block w-full cursor-zoom-in overflow-hidden rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <img src={gallery[activeIdx] || FALLBACK} alt={`${v.make} ${v.model}`} className="aspect-[16/11] w-full object-cover" />
            </button>
            {gallery.length > 1 && (
              <div className="mt-3 grid grid-cols-5 gap-2" data-testid="vehicle-gallery">
                {gallery.slice(0, 10).map((img, i) => (
                  <button key={i} type="button" onClick={() => setImgIdx(i)} data-testid={`thumb-${i}`} className={`aspect-video overflow-hidden rounded-md border-2 transition-colors ${i === activeIdx ? "border-rose-600" : "border-slate-200 dark:border-slate-800 hover:border-slate-300"}`}>
                    <img src={img} alt="" className="h-full w-full object-cover" onError={(e) => { e.target.style.opacity = 0.3; }} />
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            {v.broker_verified ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700">
                <ShieldCheck className="h-4 w-4" /> Broker Verified
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-bold text-amber-700">
                <AlertTriangle className="h-4 w-4" /> Requires verification
              </span>
            )}
            <p className="mt-4 text-xs font-bold uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">{v.year} · Brand new</p>
            <h1 className="font-display text-4xl font-black tracking-tighter text-slate-950 dark:text-slate-50">{v.make} {v.model}</h1>
            <p className="mt-1 text-lg text-slate-600 dark:text-slate-300">{v.trim} {v.body_type && `· ${v.body_type}`}</p>
            <div className="mt-5 flex items-baseline gap-2">
              <span className="font-display text-3xl font-bold text-slate-950 dark:text-slate-50">{currency(v.msrp)}</span>
              <span className="text-sm text-slate-400 dark:text-slate-500">MSRP · final quote after verification</span>
            </div>

            {promo && !sold && (
              <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900/50 dark:bg-rose-950/20" data-testid="detail-promo-callout">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-bold ${PROMO_TYPES[promo.promo_type]?.cls || "bg-rose-600 text-white"}`} data-testid={`detail-promo-badge-${promo.id}`}><Tag className="h-3.5 w-3.5" /> {promoBadge(promo)}</span>
                  {expiryLabel(promo) && <span className="rounded-full bg-amber-500 px-2 py-0.5 text-[10px] font-bold uppercase text-white" data-testid="detail-promo-expiry">{expiryLabel(promo)}</span>}
                </div>
                <p className="mt-2 text-sm font-bold text-slate-900 dark:text-slate-100">{promo.title}</p>
                {promo.description && <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300">{promo.description}</p>}
                <button onClick={() => navigate("/specials")} className="mt-1.5 text-xs font-bold text-rose-600 hover:underline" data-testid="detail-promo-link">See all specials →</button>
              </div>
            )}

            {v.option_packages?.length > 0 && (
              <div className="mt-5 flex flex-wrap gap-2">
                {v.option_packages.map((p, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs font-semibold text-slate-600 dark:text-slate-300"><Package className="h-3.5 w-3.5" />{p}</span>
                ))}
              </div>
            )}

            <div className="mt-6 grid grid-cols-2 gap-3">
              {specs.map((s, i) => (
                <div key={i} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3">
                  <p className="flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-slate-400 dark:text-slate-500"><s.icon className="h-3.5 w-3.5" />{s.label}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-800 dark:text-slate-200">{s.value}</p>
                </div>
              ))}
            </div>

            {v.description && <p className="mt-5 text-sm leading-relaxed text-slate-600 dark:text-slate-300">{v.description}</p>}

            <div className="mt-7">
              <Button data-testid="request-vehicle-btn" disabled={busy || sold} onClick={request} className="w-full rounded-md bg-slate-950 py-6 text-base font-semibold hover:bg-slate-800 disabled:opacity-50">
                {sold ? "Currently unavailable" : busy ? "Submitting…" : "Request this vehicle"}
              </Button>
              <p className="mt-2 text-center text-xs text-slate-400 dark:text-slate-500">A broker will manually verify availability before any quote or financing.</p>
            </div>
          </div>
        </div>

        {(sold || similar.length > 0) && (
          <div className="mt-14">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">{sold ? "Similar available vehicles" : "You might also like"}</h2>
            <div className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {similar.map((s) => <VehicleCard key={s.id} v={s} />)}
            </div>
          </div>
        )}
      </div>
      {lightboxOpen && (
        <div data-testid="lightbox" className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
          onClick={() => setLightboxOpen(false)}
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => { if (touchX.current == null) return; const dx = e.changedTouches[0].clientX - touchX.current; if (dx > 40) showPrev(); else if (dx < -40) showNext(); touchX.current = null; }}>
          <button type="button" data-testid="lightbox-close" onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }} className="absolute right-4 top-4 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"><X className="h-6 w-6" /></button>
          {gallery.length > 1 && (
            <>
              <button type="button" data-testid="lightbox-prev" onClick={(e) => { e.stopPropagation(); showPrev(); }} className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"><ChevronLeft className="h-7 w-7" /></button>
              <button type="button" data-testid="lightbox-next" onClick={(e) => { e.stopPropagation(); showNext(); }} className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 p-2 text-white transition-colors hover:bg-white/20"><ChevronRight className="h-7 w-7" /></button>
            </>
          )}
          <img data-testid="lightbox-image" src={gallery[activeIdx] || FALLBACK} alt={`${v.make} ${v.model}`} className="max-h-[88vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-sm font-semibold text-white">{activeIdx + 1} / {gallery.length}</div>
        </div>
      )}
    </div>
  );
}
